import { useEffect, useRef } from 'react'
import gsap from 'gsap'

/**
 * Scoped GSAP with automatic cleanup.
 *
 * gsap.context + revert is the correct React pattern: without it, StrictMode's double
 * mount leaves duplicate tweens running and elements stuck mid-animation.
 *
 * Reduced motion is honoured by collapsing the timeline rather than skipping it, so
 * final states still settle and nothing is left invisible.
 */
export function useGsap(
  build: (ctx: { el: HTMLElement }) => void,
  deps: unknown[] = [],
) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const ctx = gsap.context(() => {
      if (reduced) gsap.globalTimeline.timeScale(200)
      build({ el })
    }, el)

    return () => ctx.revert()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return ref
}

/**
 * Counts a number up on FIRST MOUNT ONLY.
 *
 * Deliberately not re-run on updates: a KPI that re-animates on every poll is noise,
 * not feedback (docs/08).
 */
export function useCountUp(target: number, duration = 0.7) {
  const ref = useRef<HTMLSpanElement>(null)
  const done = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || done.current) return
    done.current = true

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.textContent = String(target)
      return
    }

    const obj = { v: 0 }
    const tween = gsap.to(obj, {
      v: target,
      duration,
      ease: 'power2.out',
      snap: { v: 1 },
      onUpdate: () => { el.textContent = String(Math.round(obj.v)) },
    })
    return () => { tween.kill() }
  }, [target, duration])

  return ref
}
